import Controller, { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { action } from '@ember/object';
import { empty, or, readOnly } from '@ember/object/computed';
import { dropTask, enqueueTask, task } from 'ember-concurrency';
import { getClassificationIdsFromObject, mapClassificationsToCategories } from 'sh-www/helpers/admin/map-classifications-to-categories';
import { showModal } from 'sh-www/utils/modal-controls';
import { CLASSIFICATION_TYPES } from 'sh-www/models/classification';
import {
  PROJECTDIGEST_VERIFICATION_STATUS,
  PROJECTDIGEST_VERIFICATION_METHODS,
} from 'sh-www/models/projectdigest-verification';

import { USERS_LIST_COMPONENT } from 'sh-www/components/users-list';
import { USER } from 'sh-www/models/user';

class AdminVettingQueueController extends Controller {
  @controller('admin.user.rate') rateController;

  @service('state-managers/application') applicationStateManager;
  @service('commands/classification-commands') classificationCommandService;
  @service('commands/projectdigest-commands') projectDigestCommandService;
  @service('error-tracker') errorTracker;
  @service('commands/user-commands') userCommandService;

  @readOnly('loadNextUser.isRunning')
  isLoadingNextUserInQueue;

  @empty('model.user')
  isQueueEmpty;

  @computed('rateController.projects.@each.mostRecentProjectDigestVerification')
  get projectdigestVerifications() {
    return this.rateController.projects.reduce((projectdigestVerifications, projectdigest) => {
      const { mostRecentProjectDigestVerification } = projectdigest;
      if (mostRecentProjectDigestVerification && mostRecentProjectDigestVerification.method !== PROJECTDIGEST_VERIFICATION_METHODS.REFERRAL) {
        projectdigestVerifications.push(projectdigest.mostRecentProjectDigestVerification);
      }
      return projectdigestVerifications;
    }, []);
  }

  @computed('loadClassificationsTask.lastSuccessful.value')
  get classificationItems() {
    return mapClassificationsToCategories(this.loadClassificationsTask.lastSuccessful?.value);
  }

  @computed('model.user.approval')
  get userMenuOptions () {
    const { user } = this.model;
    const menuActions = [
      USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED,
    ];

    const menuOptions = [];
    menuActions.forEach((menuAction) => {
      const menuOption = this.menuOptionForUser(user, menuAction);
      if (menuOption) {
        menuOptions.addObject(menuOption);
      }
    });

    return menuOptions;
  }

  @or('invokeMenu.isRunning','loadNextUser.isRunning', 'removeFromQueueTask.isRunning')
  disableUserMenuActions;

  @computed('rateController.finalServiceRatings','saveServiceRatingsTask.isRunning','isLoadingNextUserInQueue.isRunning', 'removeFromQueueTask.isRunning')
  get disableSaveAction() {
    return Object.keys(this.rateController.finalServiceRatings).length === 0
      || this.saveServiceRatingsTask.isRunning
      || this.isLoadingNextUserInQueue.isRunning
      || this.removeFromQueueTask.isRunning;
  }

  @computed('saveServiceRatingsTask.isRunning','isLoadingNextUserInQueue.isRunning', 'removeFromQueueTask.isRunning')
  get disableRemoveFromQueueAction() {
    return this.saveServiceRatingsTask.isRunning || this.isLoadingNextUserInQueue.isRunning || this.removeFromQueueTask.isRunning;
  }

  menuOptionForUser(user, menuAction) {
    switch (menuAction) {
      case USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR:
        return {
          type: USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
        };
      case USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED: {
        let title;
        if (user.approval === USER.APPROVAL.UN_APPROVED) {
          title = 'Un-ban';
        } else if (user.approval === USER.APPROVAL.UNDEFINED) {
          title = 'Approve';
        } else {
          title = 'Ban';
        }

        return {
          title,
          onClick: () => {
            this.invokeMenu.perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED);
          },
        };
      }
      default:
        return null;
    }
  }

  @enqueueTask
  *invokeMenu(user, menuAction) {
    switch (menuAction) {
      case USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED:
        yield this.userCommandService.toggleApproval(user);

        // get the next user to be rated because unrated users are removed from the queue
        yield this.loadNextUser.perform();
        break;
    }
  }

  @dropTask
  *saveServiceRatingsTask(user, serviceRatings, allServices) {
    // format for request to include all services
    const allServiceRatings = allServices.reduce((accum, { id }) => {
      accum[id] = serviceRatings[id] ?? 0;

      return accum;
    }, {});

    try {
      yield this.userCommandService.setServiceRatings(user, allServiceRatings);

      // reset props for next queue user
      this.rateController.setProperties({
        adminAddedUnlistedServices: [],
        projects: [],
        pagination: null,
        selectedUnlistedService: null,
        serviceRatings: {},
        serviceRatingOverrides: {},
      });

      // get the next user to be rated
      yield this.loadNextUser.perform();
    } catch (e) {
      this.errorTracker.error(e, this, 'Failed to save service rating on a vetting queue user');
      this.applicationStateManager.addToast('Failed to rate user. Please reload the page and try again.');
    }
  }

  @task
  *setVerificationStatusTask(projectdigestVerificationId, projectdigestId, status) {
    yield this.rateController.setVerificationStatusTask.perform(projectdigestVerificationId, projectdigestId, status);
    const userVettingQueueState = yield this.userCommandService.getSellerVettingQueueState(this.model.user);

    if (!userVettingQueueState.belongsInQueue) {
      yield this.loadNextUser.perform();
    }
  }

  @task
  *setBadLinkTask(projectdigestId) {
    yield this.rateController.setBadLinkTask.perform(projectdigestId);
    const userVettingQueueState = yield this.userCommandService.getSellerVettingQueueState(this.model.user);

    if (!userVettingQueueState.belongsInQueue) {
      yield this.loadNextUser.perform();
    }
  }

  @dropTask
  *removeFromQueueTask(profile) {
    try {
      yield this.userCommandService.removeUserFromVettingQueue(profile);

      // reset props for next queue user
      this.rateController.setProperties({
        adminAddedUnlistedServices: [],
        projects: [],
        pagination: null,
        selectedUnlistedService: null,
        serviceRatings: {},
        serviceRatingOverrides: {},
      });

      // get the next user to be rated
      yield this.loadNextUser.perform();
    } catch (e) {
      this.errorTracker.error(e, this, 'Failed to remove user from vetting queue');
      this.applicationStateManager.addToast('Failed to remove user from vetting queue. Please reload the page and try again.');
    }
  }

  @task
  *loadNextUser() {
    const nextQueueUser = yield this.userCommandService.getAdminRateQueueUser();

    this.set('model.user', nextQueueUser);

    // if there is another user to be vetted
    if (nextQueueUser) {
      // load their work samples
      this.rateController.set('projects', []);
      yield this.rateController.loadProjectsWaiter.perform();
    }
  }

  @task
  *loadClassificationsTask() {
    try {
      return yield this.classificationCommandService.loadClassifications({
        filters: {
          types: [
            CLASSIFICATION_TYPES.TYPE_DEFAULT,
            CLASSIFICATION_TYPES.TYPE_ADMIN_ONLY,
          ],
        },
      });
    } catch (error) {
      this.errorTracker.error('loadClassificationsTask - failed to fetch classifications', this, error);
      this.applicationStateManager.addToast("Failed to load classifications. Please try again.");
    }
  }

  @enqueueTask
  *updateProjectdigestClassifications(classificationIds, projectdigestId) {
    try {
      return yield this.projectDigestCommandService.updateProjectdigestClassifications(projectdigestId, classificationIds)
    } catch (error) {
      this.errorTracker.error(`updateProjectdigestClassifications - failed to update classifications for projectdigest: ${projectdigestId}`, this, error);
      this.applicationStateManager.addToast("Failed to update classifications for project. Please try again.");
    }
  }

  @enqueueTask
  *updateProjectdigestFeatured(featured, projectdigestId) {
    try {
      return yield this.projectDigestCommandService.updateProjectdigestFeaturedStatus(projectdigestId, featured);
    } catch (error) {
      this.errorTracker.error(`updateProjectdigestFeatured - failed to update featured status for projectdigest: ${projectdigestId}`, this, error);
      this.applicationStateManager.addToast("Failed to update featured status for project. Please try again.");
    }
  }

  @action
  removeFromQueueClicked() {
    showModal(this, 'confirmation', {
      titleText: 'Remove From Queue?',
      bodyText: 'This user will be removed from the vetting queue. You will still be able to vet them from their profile.',
      actionButtonText: 'Remove',
      confirmAction: () => {
        this.removeFromQueueTask.perform(this.model.user.profile);
      },
      cancelButtonText: 'Cancel',
    })
  }

  @action
  setFeaturedChanged(newValue, projectdigestId) {
    this.updateProjectdigestFeatured.perform(newValue, projectdigestId);
  }

  @action
  setClassificationsChanged(newValues, projectdigestId) {
    const classificationIds = getClassificationIdsFromObject(newValues);
    this.updateProjectdigestClassifications.perform(classificationIds, projectdigestId);
  }
}

export default AdminVettingQueueController;
